import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CalendarRange,
  Camera,
  FileCheck,
  Hash,
  Image as ImageIcon,
  Info,
  KeyRound,
  Layers,
  Loader2,
  Mail,
  MapPinned,
  MapPinOff,
  Save,
  Scale,
  Settings as SettingsIcon,
  Smartphone,
  Sparkles,
  Star,
  Ticket,
  TrafficCone,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminEventOverview from "@/pages/admin/AdminEventOverview";
import AdminEventTicketTypes from "@/pages/admin/AdminEventTicketTypes";
import AdminEventTicketDesign from "@/pages/admin/AdminEventTicketDesign";
import AdminEventCompanion from "@/pages/admin/AdminEventCompanion";
import AdminEventMailings from "@/pages/admin/AdminEventMailings";
import AdminEventRegistration from "@/pages/admin/AdminEventRegistration";
import AdminEventTimeslots from "@/pages/admin/AdminEventTimeslots";

// Sprint 09J — the event admin page was a single long stacked page (basic fields +
// publication toggles + ticket types + communicatie + the full Companion/StapKompas
// card tree, all always rendered top to bottom). This file is now a workspace SHELL
// around the exact same pieces — every section below is either the original
// "Basisgegevens"/"Publicatie" card content (unchanged fields, unchanged onSubmit)
// or an existing component moved as-is (AdminEventTicketTypes/AdminEventMailings/
// AdminEventCompanion — none of their own internals were touched). Sections are
// toggled via CSS visibility, never unmounted, so no child component's fetched data
// or in-progress form state (e.g. a draft mailing mid-wizard) is ever lost by
// switching tabs — see the "UNSAVED CHANGES" section of the sprint spec.

const STATUS_OPTIONS = [
  ["draft", "Concept"],
  ["published", "Gepubliceerd"],
  ["sold_out", "Uitverkocht"],
  ["cancelled", "Geannuleerd"],
  ["archived", "Gearchiveerd"],
];

const BASE_SECTIONS = [
  { key: "overview", label: "Overzicht", icon: Layers },
  { key: "general", label: "Algemeen", icon: Calendar },
  { key: "tickets", label: "Tickets & verkoop", icon: Ticket },
  { key: "ticket-design", label: "Ticketontwerp", icon: ImageIcon },
  { key: "communication", label: "Communicatie", icon: Mail },
  { key: "settings", label: "Instellingen", icon: SettingsIcon },
];
const STAPKOMPAS_SECTION = { key: "stapkompas", label: "StapKompas", icon: MapPinned };
const REGISTRATION_SECTION = { key: "registration", label: "Aanmeldformulier", icon: CalendarRange };
const TIMESLOTS_SECTION = { key: "timeslots", label: "Tijdsloten", icon: Calendar };

function toDateTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

function InputField({ label, id, required, ...rest }) {
  return (
    <label htmlFor={id} className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label} {required && <span className="text-svj-pink">*</span>}
      </span>
      <input
        id={id}
        {...rest}
        className={`mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base ${rest.className || ""}`}
      />
    </label>
  );
}

const emptyForm = () => ({
  title: "",
  slug: "",
  description: "",
  location: "",
  address: "",
  starts_at: "",
  ends_at: "",
  cover_image_url: "",
  status: "draft",
  featured: false,
  organization_id: "",
  companion_app_enabled: false,
  incident_reporting_enabled: false,
  scavenger_hunt_enabled: false,
  scavenger_hunt_event_codeword_enabled: false,
  live_flow_enabled: false,
  multi_day_registration_enabled: false,
  start_numbers_enabled: false,
  timeslot_booking_enabled: false,
  timeslot_manual_bookings_enabled: false,
  traffic_control_enabled: false,
  traffic_post_switch_lead_meters: 300,
  checkout_hide_address_fields: false,
  participant_terms_enabled: false,
  participant_terms_url: "",
  organizer_terms_enabled: false,
  organizer_terms_text: "",
  info_link_enabled: false,
  info_link_url: "",
});

export default function AdminEventForm() {
  const { eventId } = useParams();
  const isNew = !eventId || eventId === "new";
  const { authAxios, me } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [rawEvent, setRawEvent] = useState(null);
  const [ticketImageUrl, setTicketImageUrl] = useState(null);
  const [ticketTheme, setTicketTheme] = useState(null);
  const [ticketBackgroundImageUrl, setTicketBackgroundImageUrl] = useState(null);
  const savedSnapshot = useRef(JSON.stringify(emptyForm()));
  const coverInputRef = useRef(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const dirty = !isNew && JSON.stringify(form) !== savedSnapshot.current;

  const sections = useMemo(() => {
    const middle = [...BASE_SECTIONS.slice(0, 5)];
    if (form.companion_app_enabled) middle.push(STAPKOMPAS_SECTION);
    if (form.multi_day_registration_enabled) middle.push(REGISTRATION_SECTION);
    if (form.timeslot_booking_enabled) middle.push(TIMESLOTS_SECTION);
    return [...middle, BASE_SECTIONS[5]];
  }, [form.companion_app_enabled, form.multi_day_registration_enabled, form.timeslot_booking_enabled]);
  const activeSection = sections.some((s) => s.key === searchParams.get("section"))
    ? searchParams.get("section")
    : "overview";
  const setActiveSection = (key) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("section", key);
    return next;
  });

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post("/admin/events/upload-cover", fd);
      setForm((f) => ({ ...f, cover_image_url: data.cover_image_url }));
      toast.success("Cover geüpload");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/organizations");
        setOrgs(data.items || []);
        if (isNew && (data.items || []).length && !me?.is_superadmin) {
          setForm((f) => ({ ...f, organization_id: data.items[0].id }));
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      }
      if (isNew) return;
      try {
        const { data } = await authAxios.get("/admin/events");
        const ev = (data.items || []).find((x) => x.id === eventId);
        if (!ev) {
          toast.error("Event niet gevonden");
          navigate("/admin/events");
          return;
        }
        const next = {
          title: ev.title || "",
          slug: ev.slug || "",
          description: ev.description || "",
          location: ev.location || "",
          address: ev.address || "",
          starts_at: toDateTimeLocal(ev.starts_at),
          ends_at: toDateTimeLocal(ev.ends_at),
          cover_image_url: ev.cover_image_url || "",
          status: ev.status || "draft",
          featured: !!ev.featured,
          organization_id: ev.organization_id || "",
          companion_app_enabled: !!ev.companion_app_enabled,
          incident_reporting_enabled: !!ev.incident_reporting_enabled,
          scavenger_hunt_enabled: !!ev.scavenger_hunt_enabled,
          scavenger_hunt_event_codeword_enabled: !!ev.scavenger_hunt_event_codeword_enabled,
          live_flow_enabled: !!ev.live_flow_enabled,
          multi_day_registration_enabled: !!ev.multi_day_registration_enabled,
          start_numbers_enabled: !!ev.start_numbers_enabled,
          timeslot_booking_enabled: !!ev.timeslot_booking_enabled,
          timeslot_manual_bookings_enabled: !!ev.timeslot_manual_bookings_enabled,
          traffic_control_enabled: !!ev.traffic_control_enabled,
          traffic_post_switch_lead_meters: ev.traffic_post_switch_lead_meters ?? 300,
          checkout_hide_address_fields: !!ev.checkout_hide_address_fields,
          participant_terms_enabled: !!ev.participant_terms_enabled,
          participant_terms_url: ev.participant_terms_url || "",
          organizer_terms_enabled: !!ev.organizer_terms_enabled,
          organizer_terms_text: ev.organizer_terms_text || "",
          info_link_enabled: !!ev.info_link_enabled,
          info_link_url: ev.info_link_url || "",
        };
        setForm(next);
        savedSnapshot.current = JSON.stringify(next);
        setRawEvent(ev);
        setTicketImageUrl(ev.ticket_image_url || null);
        setTicketTheme(ev.ticket_theme || null);
        setTicketBackgroundImageUrl(ev.ticket_background_image_url || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Warn on a real browser tab close/refresh with unsaved changes — never a silent
  // data loss, per the sprint's explicit "UNSAVED CHANGES" requirement.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.starts_at) {
      toast.error("Titel en startdatum zijn verplicht");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      description: form.description || "",
      location: form.location || "",
      address: form.address || "",
      starts_at: toIso(form.starts_at),
      ends_at: form.ends_at ? toIso(form.ends_at) : null,
      cover_image_url: form.cover_image_url || null,
      status: form.status,
      featured: form.featured,
    };
    if (me?.is_superadmin) payload.organization_id = form.organization_id || null;
    try {
      if (isNew) {
        const { data } = await authAxios.post("/admin/events", payload);
        toast.success("Event aangemaakt");
        navigate(`/admin/events/${data.id}`);
      } else {
        await authAxios.patch(`/admin/events/${eventId}`, {
          ...payload,
          companion_app_enabled: form.companion_app_enabled,
          incident_reporting_enabled: form.incident_reporting_enabled,
          scavenger_hunt_enabled: form.scavenger_hunt_enabled,
          scavenger_hunt_event_codeword_enabled: form.scavenger_hunt_event_codeword_enabled,
          live_flow_enabled: form.live_flow_enabled,
          multi_day_registration_enabled: form.multi_day_registration_enabled,
          start_numbers_enabled: form.start_numbers_enabled,
          timeslot_booking_enabled: form.timeslot_booking_enabled,
          timeslot_manual_bookings_enabled: form.timeslot_manual_bookings_enabled,
          traffic_control_enabled: form.traffic_control_enabled,
          traffic_post_switch_lead_meters: form.traffic_post_switch_lead_meters,
          checkout_hide_address_fields: form.checkout_hide_address_fields,
          participant_terms_enabled: form.participant_terms_enabled,
          participant_terms_url: form.participant_terms_url.trim() || null,
          info_link_enabled: form.info_link_enabled,
          info_link_url: form.info_link_url.trim() || null,
          // Superadmin-locked server-side (see admin_update_event) -- only sent when
          // this admin can actually change it, so a non-superadmin re-saving the rest
          // of the form never trips the backend's changed-value check by resending a
          // stale copy of a field their own UI never let them edit.
          ...(me?.is_superadmin
            ? {
                organizer_terms_enabled: form.organizer_terms_enabled,
                organizer_terms_text: form.organizer_terms_text.trim() || null,
              }
            : {}),
        });
        savedSnapshot.current = JSON.stringify(form);
        toast.success("Event opgeslagen");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmLeaveIfDirty = (e) => {
    if (dirty && !window.confirm("Je hebt niet-opgeslagen wijzigingen. Toch terug naar events?")) {
      e.preventDefault();
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-svj-cyan" />
      </div>
    );
  }

  const orgName = orgs.find((o) => o.id === form.organization_id)?.name || rawEvent?.organizations?.name || null;

  return (
    <div data-testid="admin-event-form">
      <Link to="/admin/events" onClick={confirmLeaveIfDirty} className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-4">
        <ArrowLeft className="h-4 w-4" /> Terug naar events
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">
            {isNew ? "Nieuw" : "Bewerken"}
          </div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold text-white">
            {isNew ? "Nieuw event" : form.title || "Event"}
          </h1>
        </div>
        {!isNew && (
          <button
            type="submit"
            form="admin-event-basics-form"
            disabled={saving}
            data-testid="event-form-submit-sticky"
            className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 px-6 font-bold text-sm transition-all ${
              saving
                ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                : dirty
                ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                : "bg-svj-bg border border-svj-border text-slate-300"
            }`}
          >
            {saving ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Save className="h-4.5 w-4.5" />}
            {dirty ? "Opslaan" : "Opgeslagen"}
          </button>
        )}
      </div>

      {!isNew && (
        <div
          className="mt-6 flex items-center gap-1.5 overflow-x-auto pb-1"
          data-testid="admin-event-section-nav"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {sections.map((s) => {
            const Icon = s.icon;
            const active = activeSection === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveSection(s.key)}
                data-testid={`admin-event-section-${s.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold whitespace-nowrap transition-colors ${
                  active ? "bg-svj-cyan text-svj-bg" : "bg-svj-bg border border-svj-border text-slate-300 hover:text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {s.label}
              </button>
            );
          })}
        </div>
      )}

      <div className={isNew || activeSection === "overview" ? "mt-8" : "hidden"}>
        {!isNew && (
          <AdminEventOverview form={form} rawEvent={rawEvent} orgName={orgName} sections={sections} onNavigate={setActiveSection} />
        )}
      </div>

      {/* noValidate: Algemeen/Instellingen fields (including the `required` title/
          starts_at inputs) stay in the DOM but are CSS-hidden when their section
          isn't active, so a sticky Save click from another tab must never depend on
          the browser's native validation bubble (which can't anchor to a hidden
          field and may silently block submission with no visible feedback) — the
          equivalent check already runs explicitly in onSubmit and shows a toast. */}
      <form id="admin-event-basics-form" onSubmit={onSubmit} noValidate className="mt-8">
        <div className={isNew || activeSection === "general" ? "grid grid-cols-1 lg:grid-cols-3 gap-8" : "hidden"}>
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
              <div className="text-sm font-semibold text-white uppercase tracking-wider">Basisgegevens</div>
              <InputField
                label="Titel"
                id="title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="event-form-title"
              />
              <InputField
                label="Slug (URL)"
                id="slug"
                placeholder="Wordt automatisch gemaakt als je dit leeg laat"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                data-testid="event-form-slug"
              />
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Beschrijving</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={5}
                  data-testid="event-form-description"
                  className="mt-1 w-full px-4 py-3 rounded-lg bg-svj-bg border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base resize-y"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Tip: voeg een link toe met <code className="text-slate-400">[linktekst](https://...)</code>, of
                  plak een link gewoon rechtstreeks in de tekst — beide worden op de eventpagina klikbaar.
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Locatie"
                  id="location"
                  placeholder="Bijv. Havenkant Biddinghuizen"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="event-form-location"
                />
                <InputField
                  label="Adres"
                  id="address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="event-form-address"
                />
                <InputField
                  label="Start"
                  id="starts_at"
                  type="datetime-local"
                  required
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  data-testid="event-form-starts-at"
                />
                <InputField
                  label="Einde"
                  id="ends_at"
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                  data-testid="event-form-ends-at"
                />
              </div>
              <div>
                <InputField
                  label="Cover-afbeelding URL"
                  id="cover_image_url"
                  placeholder="https://…"
                  value={form.cover_image_url}
                  onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                  data-testid="event-form-cover"
                />
                <div className="mt-2 flex items-center gap-3">
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={onCoverFile}
                    className="hidden"
                    data-testid="event-cover-input"
                  />
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploadingCover}
                    data-testid="event-cover-upload"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-1.5 px-4 text-xs hover:bg-svj-cyan/10 transition-colors"
                  >
                    {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                    {form.cover_image_url ? "Vervang cover" : "Upload cover"}
                  </button>
                  <span className="text-[10px] text-slate-500">PNG/JPG/WEBP/GIF · max 5MB</span>
                </div>
              </div>
              {form.cover_image_url && (
                <img src={form.cover_image_url} alt="preview" className="w-full aspect-[16/6] object-cover rounded-lg border border-svj-border" />
              )}
            </div>
          </div>

          {isNew && (
            <div className="lg:col-span-1 space-y-6">
              {me?.is_superadmin && (
                <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
                  <div className="text-sm font-semibold text-white uppercase tracking-wider">Organisatie</div>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Organisatie *</span>
                    <select
                      value={form.organization_id}
                      onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                      data-testid="event-form-organization-new"
                      className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                    >
                      <option value="">Kies organisatie…</option>
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              <button
                type="submit"
                disabled={saving}
                data-testid="event-form-submit"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                  saving
                    ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                }`}
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                Aanmaken
              </button>
              <p className="text-xs text-slate-500">
                Na het aanmaken krijg je toegang tot tickettypes, ticketontwerp, communicatie en (indien van toepassing)
                StapKompas.
              </p>
            </div>
          )}
        </div>

        {!isNew && (
          <div className={activeSection === "settings" ? "mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8" : "hidden"}>
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-svj-card border border-svj-border rounded-2xl p-6 space-y-4">
                <div className="text-sm font-semibold text-white uppercase tracking-wider">Publicatie</div>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Status</span>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    data-testid="event-form-status"
                    className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                  >
                    {STATUS_OPTIONS.map(([k, l]) => (
                      <option key={k} value={k}>{l}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, featured: !form.featured })}
                  data-testid="event-form-featured"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.featured
                      ? "bg-svj-orange/20 border-svj-orange/60 text-svj-orange"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <Star className={`h-4 w-4 ${form.featured ? "fill-svj-orange" : ""}`} />
                  {form.featured ? "Uitgelicht" : "Zet uitgelicht"}
                </button>
                <button
                  type="button"
                  onClick={() => me?.is_superadmin && setForm({ ...form, companion_app_enabled: !form.companion_app_enabled })}
                  disabled={!me?.is_superadmin}
                  title={me?.is_superadmin ? undefined : "Alleen superadmin mag StapKompas aan- of uitzetten"}
                  data-testid="event-form-companion-app"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.companion_app_enabled
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  } ${!me?.is_superadmin ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <Smartphone className="h-4 w-4" />
                  {form.companion_app_enabled ? "Companion-app aan" : "Zet companion-app aan"}
                </button>
                {!me?.is_superadmin && (
                  <p className="text-[11px] text-slate-500">
                    Alleen een superadmin kan StapKompas aan- of uitzetten voor dit event.
                  </p>
                )}
                {form.companion_app_enabled && (
                  <p className="text-[11px] text-slate-500">
                    De StapKompas-sectie is nu zichtbaar in de navigatie hierboven.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => me?.is_superadmin && setForm({ ...form, multi_day_registration_enabled: !form.multi_day_registration_enabled })}
                  disabled={!me?.is_superadmin}
                  title={me?.is_superadmin ? undefined : "Alleen superadmin mag meerdaagse aanmelding aan- of uitzetten"}
                  data-testid="event-form-multi-day-registration"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.multi_day_registration_enabled
                      ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  } ${!me?.is_superadmin ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <CalendarRange className="h-4 w-4" />
                  {form.multi_day_registration_enabled ? "Meerdaagse aanmelding aan" : "Zet meerdaagse aanmelding aan"}
                </button>
                {!me?.is_superadmin && (
                  <p className="text-[11px] text-slate-500">
                    Alleen een superadmin kan meerdaagse aanmelding aan- of uitzetten voor dit event.
                  </p>
                )}
                {form.multi_day_registration_enabled && (
                  <p className="text-[11px] text-slate-500">
                    De sectie "Aanmeldformulier" is nu zichtbaar in de navigatie hierboven. Deze functie is
                    volledig los van StapKompas en heeft geen effect op normale ticketverkoop.
                  </p>
                )}
                {form.multi_day_registration_enabled && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, start_numbers_enabled: !form.start_numbers_enabled })}
                    data-testid="event-form-start-numbers"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.start_numbers_enabled
                        ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <Hash className="h-4 w-4" />
                    {form.start_numbers_enabled ? "Startnummers aan" : "Zet startnummers aan"}
                  </button>
                )}
                {form.multi_day_registration_enabled && form.start_numbers_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Nieuwe tickets voor dit event krijgen automatisch een volgend startnummer, beginnend bij 1.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => me?.is_superadmin && setForm({ ...form, timeslot_booking_enabled: !form.timeslot_booking_enabled })}
                  disabled={!me?.is_superadmin}
                  title={me?.is_superadmin ? undefined : "Alleen superadmin mag tijdslot-boekingen aan- of uitzetten"}
                  data-testid="event-form-timeslot-booking"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.timeslot_booking_enabled
                      ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  } ${!me?.is_superadmin ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <Calendar className="h-4 w-4" />
                  {form.timeslot_booking_enabled ? "Tijdslot-boekingen aan" : "Zet tijdslot-boekingen aan"}
                </button>
                {!me?.is_superadmin && (
                  <p className="text-[11px] text-slate-500">
                    Alleen een superadmin kan tijdslot-boekingen aan- of uitzetten voor dit event.
                  </p>
                )}
                {form.timeslot_booking_enabled && (
                  <p className="text-[11px] text-slate-500">
                    De sectie "Tijdsloten" is nu zichtbaar in de navigatie hierboven. Klanten reserveren een
                    datum + tijdsblok met een eigen prijs per blok, bijvoorbeeld voor Sinterklaas huisbezoeken.
                  </p>
                )}
                {form.timeslot_booking_enabled && (
                  <button
                    type="button"
                    onClick={() => me?.is_superadmin && setForm({ ...form, timeslot_manual_bookings_enabled: !form.timeslot_manual_bookings_enabled })}
                    disabled={!me?.is_superadmin}
                    title={me?.is_superadmin ? undefined : "Alleen superadmin mag handmatige blokkering van tijdsloten aan- of uitzetten"}
                    data-testid="event-form-timeslot-manual-bookings"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.timeslot_manual_bookings_enabled
                        ? "bg-svj-purple/20 border-svj-purple/60 text-svj-purple"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    } ${!me?.is_superadmin ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    <Hash className="h-4 w-4" />
                    {form.timeslot_manual_bookings_enabled ? "Handmatige blokkering aan" : "Zet handmatige blokkering aan"}
                  </button>
                )}
                {form.timeslot_booking_enabled && !me?.is_superadmin && (
                  <p className="text-[11px] text-slate-500">
                    Alleen een superadmin kan handmatige blokkering van tijdsloten aan- of uitzetten voor dit event.
                  </p>
                )}
                {form.timeslot_booking_enabled && form.timeslot_manual_bookings_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Je kunt nu "al gemaakte afspraken" (bv. telefonisch geregeld) rechtstreeks toevoegen via
                    "Handmatig ticket aanmaken" op het tabblad Tickets &amp; verkoop — dat blokkeert het tijdslot
                    zonder dat de klant moet afrekenen.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => me?.is_superadmin && setForm({ ...form, traffic_control_enabled: !form.traffic_control_enabled })}
                  disabled={!me?.is_superadmin}
                  title={me?.is_superadmin ? undefined : "Alleen superadmin mag StapKompas Verkeer aan- of uitzetten"}
                  data-testid="event-form-traffic-control"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.traffic_control_enabled
                      ? "bg-amber-500/20 border-amber-500/60 text-amber-300"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  } ${!me?.is_superadmin ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <TrafficCone className="h-4 w-4" />
                  {form.traffic_control_enabled ? "StapKompas Verkeer aan" : "Zet StapKompas Verkeer aan"}
                </button>
                {!me?.is_superadmin && (
                  <p className="text-[11px] text-slate-500">
                    Alleen een superadmin kan StapKompas Verkeer aan- of uitzetten voor dit event.
                  </p>
                )}
                {form.traffic_control_enabled && (
                  <>
                    <p className="text-[11px] text-slate-500">
                      Verkeersregelaars-accounts, verkeersposten en de live sluiter-positie worden beheerd via
                      "Verkeersregelaars" in het menu. Routes blijven bepaald in StapKompas zelf — hier richt je
                      alleen de verkeersregeling voor de al bestaande routes in.
                    </p>
                    <div>
                      <label className="block text-[11px] text-slate-400 mb-1">
                        Wisselmoment — melding X meter vóór de volgende post
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={5000}
                        value={form.traffic_post_switch_lead_meters}
                        onChange={(e) =>
                          setForm({ ...form, traffic_post_switch_lead_meters: Number(e.target.value) || 300 })
                        }
                        data-testid="event-form-traffic-post-switch-lead-meters"
                        className="w-32 h-9 px-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
                      />
                      <span className="ml-2 text-[11px] text-slate-500">meter</span>
                    </div>
                  </>
                )}
                {form.companion_app_enabled && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, incident_reporting_enabled: !form.incident_reporting_enabled })}
                    data-testid="event-form-incident-reporting"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.incident_reporting_enabled
                        ? "bg-red-500/20 border-red-500/60 text-red-300"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    {form.incident_reporting_enabled ? "Incidentmeldingen aan" : "Zet incidentmeldingen aan"}
                  </button>
                )}
                {form.companion_app_enabled && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, scavenger_hunt_enabled: !form.scavenger_hunt_enabled })}
                    data-testid="event-form-scavenger-hunt"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.scavenger_hunt_enabled
                        ? "bg-teal-500/20 border-teal-500/60 text-teal-300"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <Sparkles className="h-4 w-4" />
                    {form.scavenger_hunt_enabled ? "Zoektocht aan" : "Zet zoektocht aan"}
                  </button>
                )}
                {form.companion_app_enabled && form.scavenger_hunt_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Plaats zoekpunten bij StapKompas → Dagen &amp; routes → Punten. Deelnemers kiezen zelf of ze meedoen.
                  </p>
                )}
                {form.companion_app_enabled && form.scavenger_hunt_enabled && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        scavenger_hunt_event_codeword_enabled: !form.scavenger_hunt_event_codeword_enabled,
                      })
                    }
                    data-testid="event-form-scavenger-hunt-event-codeword"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.scavenger_hunt_event_codeword_enabled
                        ? "bg-teal-500/20 border-teal-500/60 text-teal-300"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <KeyRound className="h-4 w-4" />
                    {form.scavenger_hunt_event_codeword_enabled
                      ? "Woordpuzzel voor het hele evenement aan"
                      : "Zet woordpuzzel voor het hele evenement aan"}
                  </button>
                )}
                {form.companion_app_enabled && form.scavenger_hunt_enabled && form.scavenger_hunt_event_codeword_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Deelnemers verzamelen letters op alle dagen en vormen pas na het laatste zoekpunt één woord
                    voor het hele evenement, in plaats van een apart woord per route.
                  </p>
                )}
                {form.companion_app_enabled && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, live_flow_enabled: !form.live_flow_enabled })}
                    data-testid="event-form-live-flow"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.live_flow_enabled
                        ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <Activity className="h-4 w-4" />
                    {form.live_flow_enabled ? "Live Flow aan" : "Zet Live Flow aan"}
                  </button>
                )}
                {form.companion_app_enabled && form.live_flow_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Toont een geaggregeerde, anonieme kaart van waar de groep zich langs de route bevindt (incl.
                    kop/staart), nooit een individuele deelnemer. Bekijk deze via "Live Flow" in het menu.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, checkout_hide_address_fields: !form.checkout_hide_address_fields })}
                  data-testid="event-form-checkout-hide-address"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.checkout_hide_address_fields
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <MapPinOff className="h-4 w-4" />
                  {form.checkout_hide_address_fields ? "Verkort afrekenformulier aan" : "Zet verkort afrekenformulier aan"}
                </button>
                {form.checkout_hide_address_fields && (
                  <p className="text-[11px] text-slate-500">
                    Op de kassa worden straat, postcode en plaats verborgen — alleen naam, e-mail en telefoon
                    blijven zichtbaar. Losstaand van meerdaagse aanmelding; standaard staat dit uit en blijft het
                    volledige adresformulier gewoon zichtbaar.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, participant_terms_enabled: !form.participant_terms_enabled })}
                  data-testid="event-form-participant-terms"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.participant_terms_enabled
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <FileCheck className="h-4 w-4" />
                  {form.participant_terms_enabled ? "Deelnemersreglement aan" : "Zet deelnemersreglement aan"}
                </button>
                {form.participant_terms_enabled && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Link naar deelnemersreglement *</span>
                    <input
                      type="url"
                      value={form.participant_terms_url}
                      onChange={(e) => setForm({ ...form, participant_terms_url: e.target.value })}
                      placeholder="https://…"
                      data-testid="event-form-participant-terms-url"
                      className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Bezoekers krijgen bij het afrekenen een extra checkbox "Ik ga akkoord met het deelnemersreglement" met een link naar deze pagina.
                    </p>
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, info_link_enabled: !form.info_link_enabled })}
                  data-testid="event-form-info-link"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                    form.info_link_enabled
                      ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                      : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                  }`}
                >
                  <Info className="h-4 w-4" />
                  {form.info_link_enabled ? "Link naar meer informatie aan" : "Zet link naar meer informatie aan"}
                </button>
                {form.info_link_enabled && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Link naar meer informatie *</span>
                    <input
                      type="url"
                      value={form.info_link_url}
                      onChange={(e) => setForm({ ...form, info_link_url: e.target.value })}
                      placeholder="https://…"
                      data-testid="event-form-info-link-url"
                      className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Bezoekers zien in de bevestigingsmail en op het ticket een extra regel "Bekijk hier meer
                      informatie en routes" die naar deze link verwijst — bijvoorbeeld een routeoverzicht of de
                      eventwebsite.
                    </p>
                  </label>
                )}
                {me?.is_superadmin && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, organizer_terms_enabled: !form.organizer_terms_enabled })}
                    data-testid="event-form-organizer-terms"
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg border py-2.5 px-4 text-sm font-semibold transition-colors ${
                      form.organizer_terms_enabled
                        ? "bg-svj-cyan/20 border-svj-cyan/60 text-svj-cyan"
                        : "bg-svj-bg border-svj-border text-slate-300 hover:text-white"
                    }`}
                  >
                    <Scale className="h-4 w-4" />
                    {form.organizer_terms_enabled
                      ? "Aanvullende voorwaarden organisator aan"
                      : "Zet aanvullende voorwaarden organisator aan"}
                  </button>
                )}
                {!me?.is_superadmin && form.organizer_terms_enabled && (
                  <p className="text-[11px] text-slate-500">
                    Voor dit event gelden aanvullende voorwaarden van de organisator bovenop de
                    algemene voorwaarden. Alleen een superadmin kan deze wijzigen.
                  </p>
                )}
                {me?.is_superadmin && form.organizer_terms_enabled && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                      Tekst aanvullende voorwaarden *
                    </span>
                    <textarea
                      value={form.organizer_terms_text}
                      onChange={(e) => setForm({ ...form, organizer_terms_text: e.target.value })}
                      rows={4}
                      placeholder="Bijv.: In afwijking van artikel 4 van de algemene voorwaarden geldt voor dit evenement…"
                      data-testid="event-form-organizer-terms-text"
                      className="mt-1 w-full px-4 py-3 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Wordt op de afrekenpagina getoond als apart gelabeld blok, met een eigen
                      akkoordvinkje naast (niet in plaats van) de algemene voorwaarden. Volgens de
                      algemene voorwaarden zelf gaat deze tekst alléén voor op het punt waarop hij
                      expliciet afwijkt — verwijs dus naar het concrete artikel (zoals in het
                      voorbeeld hierboven), anders is niet duidelijk welk punt wordt overruled. Kan
                      nooit wettelijke consumentenrechten wijzigen (zoals het herroepingsrecht,
                      artikel 3) — die volgen uit de wet, niet uit deze voorwaarden. Stem de exacte
                      formulering af met de organisator en, bij twijfel, een jurist — dit wijzigt
                      StudioVoorJou's eigen verkoopvoorwaarden voor dit ene event.
                    </p>
                  </label>
                )}
                {me?.is_superadmin && (
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Organisatie *</span>
                    <select
                      value={form.organization_id}
                      onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                      data-testid="event-form-organization"
                      className="mt-1 w-full h-11 px-4 rounded-lg bg-svj-bg border border-svj-border text-white focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                    >
                      <option value="">Kies organisatie…</option>
                      {orgs.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="event-form-submit"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 px-6 font-bold transition-all ${
                  saving
                    ? "bg-svj-bg/60 border border-svj-border text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white hover:shadow-[0_0_24px_rgba(214,61,122,0.5)]"
                }`}
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                Opslaan
              </button>
            </div>
          </div>
        )}
      </form>

      {!isNew && (
        <>
          <div className={activeSection === "tickets" ? "mt-10" : "hidden"}>
            <AdminEventTicketTypes
              eventId={eventId}
              companionAppEnabled={form.companion_app_enabled}
              multiDayRegistrationEnabled={form.multi_day_registration_enabled}
              timeslotBookingEnabled={form.timeslot_booking_enabled}
              timeslotManualBookingsEnabled={form.timeslot_manual_bookings_enabled}
            />
          </div>

          <div className={activeSection === "ticket-design" ? "mt-10" : "hidden"}>
            <AdminEventTicketDesign
              eventId={eventId}
              authAxios={authAxios}
              ticketImageUrl={ticketImageUrl}
              coverImageUrl={form.cover_image_url}
              onChanged={setTicketImageUrl}
              ticketTheme={ticketTheme}
              onThemeChanged={setTicketTheme}
              ticketBackgroundImageUrl={ticketBackgroundImageUrl}
              onBackgroundChanged={setTicketBackgroundImageUrl}
              orgTicketTheme={orgs.find((o) => o.id === form.organization_id)?.ticket_theme || null}
              isSuperadmin={!!me?.is_superadmin}
            />
          </div>

          <div className={activeSection === "communication" ? "mt-10" : "hidden"}>
            <AdminEventMailings eventId={eventId} authAxios={authAxios} companionAppEnabled={form.companion_app_enabled} />
          </div>

          <div className={activeSection === "stapkompas" && form.companion_app_enabled ? "" : "hidden"}>
            <AdminEventCompanion
              eventId={eventId}
              authAxios={authAxios}
              enabled={form.companion_app_enabled}
              location={form.location}
              incidentReportingEnabled={form.incident_reporting_enabled}
              scavengerHuntEnabled={form.scavenger_hunt_enabled}
            />
          </div>

          <div className={activeSection === "registration" && form.multi_day_registration_enabled ? "mt-10" : "hidden"}>
            <AdminEventRegistration
              eventId={eventId}
              authAxios={authAxios}
              rawEvent={rawEvent}
              onEventChanged={(patch) => setRawEvent((r) => (r ? { ...r, ...patch } : r))}
            />
          </div>

          <div className={activeSection === "timeslots" && form.timeslot_booking_enabled ? "mt-10" : "hidden"}>
            <AdminEventTimeslots
              eventId={eventId}
              authAxios={authAxios}
              manualBookingsEnabled={!!form.timeslot_manual_bookings_enabled}
            />
          </div>
        </>
      )}
    </div>
  );
}
